// store global types
import type { TCreateSlice } from "@/store";

import type { TDtoTableExtraction } from "@/store/tableExtractions/TDtoTableExtraction";
import type {
    TDtoStandardizedTableExtraction,
    TErrorRow,
    TActiveColumnChange,
} from "./TDtoStandardizedTableExtraction";

type TActionsPublicTableExtraction = {
    resetExtractionsState: () => void;
    setActiveColumn: (label: string, extractionId: number) => void;
    updateExtractionRowsLoaded: ({
        updatedRows,
    }: {
        updatedRows: TDtoTableExtraction;
    }) => void;
    updateStandardizedExtractionRowsLoaded: ({
        updatedRows,
    }: {
        updatedRows: TDtoStandardizedTableExtraction;
    }) => void;
};

export type TSliceTableExtractions = TActionsPublicTableExtraction &
    TStateTableExtractions;

export type TStateTableExtractions = {
    extractionRowsLoaded: TDtoTableExtraction | null;
    extractionStandardizedRowsLoaded: TDtoStandardizedTableExtraction | null;
    extractionStandardizedErrors: TErrorRow[] | null;
    extractionStandardizedActiveColumns: Record<
        string,
        TActiveColumnChange
    > | null;

    extractionStandardizedMonthLabelToPeriodIdMap: Record<
        string,
        number
    > | null;
};

export const initialStateTableExtractions: TStateTableExtractions = {
    extractionRowsLoaded: null,
    extractionStandardizedRowsLoaded: null,
    extractionStandardizedErrors: null,
    extractionStandardizedActiveColumns: null,
    extractionStandardizedMonthLabelToPeriodIdMap: null,
};

export const createSliceTableExtractions: TCreateSlice<
    TSliceTableExtractions,
    Record<string, never>
> = () => set => ({
    ...initialStateTableExtractions,
    updateExtractionRowsLoaded: ({ updatedRows }) => {
        set(
            {
                extractionRowsLoaded: updatedRows,
            },
            false,
            "tableExtraction/updateExtractionRowsLoaded",
        );
    },
    updateStandardizedExtractionRowsLoaded: ({ updatedRows }) => {
        set(
            {
                extractionStandardizedRowsLoaded: updatedRows,
                extractionStandardizedErrors: updatedRows.errorRows,
                extractionStandardizedActiveColumns: updatedRows.activeColumns,
                extractionStandardizedMonthLabelToPeriodIdMap:
                    updatedRows.monthLabelToPeriodIdMap,
            },
            false,
            "tableExtraction/updateStandardizedExtractionRowsLoaded",
        );
    },
    resetExtractionsState: () => {
        set(
            initialStateTableExtractions,
            false,
            "tableExtraction/resetExtractionState",
        );
    },
    setActiveColumn: (label: string, extractionId: number) => {
        set(
            state => {
                const activeColumns =
                    state.extractionStandardizedActiveColumns || {};
                const column = activeColumns[label];

                if (!column) return state;

                return {
                    extractionStandardizedActiveColumns: {
                        ...activeColumns,
                        [label]: {
                            ...column,
                            to: extractionId,
                        },
                    },
                };
            },
            false,
            "tableExtraction/setActiveColumn",
        );
    },
});
